'use client';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  SlideProps,
  Stack,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useId } from 'react';
import { MOTION } from '../../constants';
import { IconBadge, IconBadgeTone } from '../iconBadge';
import { MaterialIcon, MaterialIconType } from '../materialIcon';

const SlideUp = (props: SlideProps) => <Slide {...props} direction="up" easing={MOTION.easeOut} />;

/**
 * Renders a dialog: a bottom sheet on phones and a centered card from `sm` up, as in customer-mobile.
 * Give it an accessible name with `ariaLabelledBy` (id of a visible title) or `title`.
 *
 * @param {string} title - Accessible name used when there is no visible title to reference.
 * @param {string} ariaLabelledBy - Id of the element that names the dialog.
 * @param {string} ariaDescribedBy - Id of the element that describes the dialog.
 * @param {string} heading - Visible title. With it the dialog lays out the shared header: optional icon badge, the
 * title (which also names the dialog) and, when `onClose` is set, a close button.
 * @param {Object} icon - Icon badge above the heading.
 * @param {SxProps} contentSx - Extra styles for the column that holds the heading and the children.
 */
export const Modal = ({
  children,
  open,
  onClose,
  maxWidth = false,
  title,
  ariaLabelledBy,
  ariaDescribedBy,
  heading,
  icon,
  contentSx,
}: {
  children: React.JSX.Element;
  open: boolean;
  onClose?: () => void;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  title?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  heading?: string;
  icon?: { icon: MaterialIconType; tone: IconBadgeTone } | null;
  contentSx?: SxProps<Theme>;
}) => {
  const theme = useTheme();
  const isSheet = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const headingId = useId();
  const labelledBy = ariaLabelledBy ?? (heading ? headingId : undefined);

  return (
    <Dialog
      data-testid="modal"
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      aria-labelledby={labelledBy}
      aria-describedby={ariaDescribedBy}
      slots={isSheet ? { transition: SlideUp } : undefined}
      slotProps={{
        transition: isSheet ? { timeout: { enter: MOTION.medium, exit: MOTION.short } } : undefined,
        // The name must sit on the role="dialog" paper; MUI otherwise points aria-labelledby at a missing DialogTitle id
        paper: {
          ...(title && !labelledBy ? { 'aria-label': title, 'aria-labelledby': undefined } : {}),
          sx: isSheet
            ? {
                m: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '85%',
                alignSelf: 'flex-end',
                borderRadius: '24px 24px 0 0',
                pb: 'env(safe-area-inset-bottom)',
              }
            : undefined,
        },
      }}
    >
      <DialogContent>
        {heading === undefined ? (
          children
        ) : (
          <>
            {onClose && (
              <IconButton
                aria-label="Cerrar"
                onClick={onClose}
                sx={{ position: 'absolute', top: 8, right: 8, width: 44, height: 44 }}
              >
                <MaterialIcon icon="close" />
              </IconButton>
            )}
            <Box
              sx={[
                {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  py: { xs: 2, md: 4 },
                  px: { xs: 2, md: 6 },
                },
                ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
              ]}
            >
              {icon && <IconBadge {...icon} />}
              {heading && (
                // A dialog title, not the page's: h2 level, sized between the page title and section headings
                <Typography id={headingId} variant="h1" component="h2" sx={{ fontSize: '1.25rem', lineHeight: 1.3 }}>
                  {heading}
                </Typography>
              )}
              {children}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Cancel and confirm buttons for a confirmation dialog. The confirm label should name the action ("Desestimar",
 * "Cerrar sesión"), destructive actions turn red, and while loading the confirm button shows a spinner and both
 * buttons stop taking clicks, so the action can't be sent twice.
 *
 * @param {string} confirmLabel - Verb for the action.
 * @param {Function} onConfirm - Runs the action.
 * @param {Function} onCancel - Closes the dialog without acting.
 * @param {string} [cancelLabel='Cancelar'] - Label of the dismiss button.
 * @param {boolean} [destructive] - Red confirm button, for actions that remove or end something.
 * @param {boolean} [loading] - Request in flight.
 * @param {string} [loadingLabel] - Confirm label while loading, e.g. "Desestimando…".
 * @param {boolean} [disabled] - Blocks confirming, e.g. until a choice is made.
 */
export const ConfirmActions = ({
  confirmLabel,
  onConfirm,
  onCancel,
  cancelLabel = 'Cancelar',
  destructive,
  loading,
  loadingLabel,
  disabled,
}: {
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
}) => (
  <Stack direction="row" sx={{ gap: 2, width: '100%', mt: 2 }}>
    <Button fullWidth variant="outlined" disabled={loading} onClick={onCancel}>
      {cancelLabel}
    </Button>
    <Button
      fullWidth
      color={destructive ? 'error' : 'primary'}
      loading={loading}
      loadingPosition="start"
      disabled={disabled}
      onClick={(event) => {
        event.preventDefault();
        onConfirm();
      }}
    >
      {loading && loadingLabel ? loadingLabel : confirmLabel}
    </Button>
  </Stack>
);
